import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { ArrowLeft, ScanLine, Share2, Send, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { qrUrl } from "@/lib/qr";
import { useAuth } from "@/context/AuthContext";
import { countryFlag } from "@/lib/places";
import { BusinessQr } from "@/components/qr/BusinessQr";
import { BusinessOffers } from "@/components/qr/BusinessOffers";
import { QrHistory } from "@/components/qr/QrHistory";

function Personal({ user, code }) {
  const nav = useNavigate();
  const share = async () => {
    const url = `${window.location.origin}/perdorues/${user.id}`;
    try { if (navigator.share) await navigator.share({ title: "GjejShqip", url }); else { await navigator.clipboard.writeText(url); toast.success("Linku u kopjua"); } } catch { /* cancelled */ }
  };
  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 text-center" data-testid="qr-personal">
      <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-emerald-500 text-white text-3xl font-bold flex items-center justify-center ring-4 ring-emerald-100">
        {user.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
      </div>
      <div className="font-extrabold text-lg text-slate-900 mt-2" translate="no">{user.nickname || `${user.name} ${user.surname || ""}`}</div>
      <div className="text-sm text-slate-500">{countryFlag(user.country)} {[user.city, user.member_no != null ? `#${String(user.member_no).padStart(2, "0")}` : ""].filter(Boolean).join(" · ")}</div>
      <div className="mt-3 mx-auto w-fit p-3 rounded-2xl border-2 border-emerald-300">
        {code ? <QRCodeSVG value={qrUrl(code)} size={200} data-testid="qr-personal-code" /> : <div className="w-[200px] h-[200px] flex items-center justify-center text-sm text-slate-400">QR personale është çaktivizuar</div>}
      </div>
      <p className="text-xs text-slate-500 mt-2">Skanoni për t'u bërë shokë, për t'më dërguar GjejKredit ose për të parë profilin tim. Kodi është live dhe ndryshon vetë.</p>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <button data-testid="qr-scan-btn" onClick={() => nav("/skano")} className="h-11 rounded-xl bg-emerald-500 text-white text-sm font-bold flex items-center justify-center gap-1.5"><ScanLine className="w-4 h-4" />Skano QR</button>
        <button data-testid="qr-share-btn" onClick={share} className="h-11 rounded-xl bg-slate-100 text-slate-700 text-sm font-bold flex items-center justify-center gap-1.5"><Share2 className="w-4 h-4" />Ndaj profilin</button>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button data-testid="qr-howto-send" onClick={() => nav("/skano?send=1")} className="h-14 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm font-bold flex items-center justify-center gap-1.5"><Send className="w-4 h-4" />Dërgo kredite</button>
        <button data-testid="qr-howto-friend" onClick={() => nav("/skano")} className="h-14 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-bold flex items-center justify-center gap-1.5"><UserPlus className="w-4 h-4" />Bëhu shok</button>
      </div>
      <ol className="text-left text-xs text-slate-600 mt-3 space-y-1.5 bg-slate-50 rounded-2xl p-3" data-testid="qr-howto">
        <li><b>1.</b> Shoku hap <b>Profili → QR-ja ime</b> në telefonin e vet.</li>
        <li><b>2.</b> Ti shtyp <b>Dërgo kredite</b> ose <b>Bëhu shok</b> dhe skano QR-në e tij me kamerë.</li>
        <li><b>3.</b> Shfaqet profili i tij: zgjidh <b>Bëhu shok</b> (ai e pranon te Njoftimet) ose <b>Dërgo kredite</b>, zgjidh sa kredite dhe konfirmo.</li>
      </ol>
    </div>
  );
}

export default function QrPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") || "me");
  const load = () => api.get("/qr/me").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => {
    if (!user) return undefined;
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [user]);
  if (!user) return <div className="p-8 text-center text-slate-500">Hyr në llogari për QR-në tënde.</div>;
  if (!d) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;
  const tabs = [["me", "QR-ja ime"], ...(d.business ? [["biz", "Biznesi"]] : []), ["history", "Historiku"]];
  return (
    <div className="px-4 pt-3 pb-28 space-y-3" data-testid="qr-page">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-slate-700 font-semibold" data-testid="qr-back"><ArrowLeft className="w-5 h-5" />QR-ja ime</button>
      <div className="flex gap-2">
        {tabs.map(([k, l]) => <button key={k} data-testid={`qr-tab-${k}`} onClick={() => setTab(k)} className={`flex-1 h-10 rounded-full text-xs sm:text-sm font-bold ${tab === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>)}
      </div>
      {tab === "me" && <Personal user={user} code={d.personal} />}
      {tab === "biz" && d.business && (<>
        <BusinessQr b={d.business} max={d.config.max_visit_points} reload={load} />
        <BusinessOffers preselect={sp.get("listing")} />
      </>)}
      {tab === "history" && <QrHistory me={user.id} business={!!d.business} />}
    </div>
  );
}
